(() => {
  const ANN_SELECTORS = [
    ".announcement-wrapper",
    "#shopify-section-announcement",
    "#shopify-section-announcement-special-page",
    "#epb_bar",
    "#epb_background"
  ];

  const isShown = (el) => {
    if (!el || el.classList.contains("hide")) return false;
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") return false;
    if (Number(style.opacity) === 0) return false;
    return el.getBoundingClientRect().height >= 1;
  };

  /**
   * Visible bottom of top chrome only (announcement strip + .site-header bar).
   * Do NOT use #shopify-section-header / mega-menu wrappers — they inflate the offset.
   */
  const getChromeBottom = () => {
    let bottom = 0;

    ANN_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        if (!isShown(el)) return;
        const rect = el.getBoundingClientRect();
        // Count only while it still occupies the top strip.
        if (rect.bottom <= 0 || rect.top >= 120) return;
        if (rect.bottom > bottom) bottom = rect.bottom;
      });
    });

    const header = document.querySelector(".site-header");
    if (header && isShown(header)) {
      const rect = header.getBoundingClientRect();
      // Layout height of the bar only (ignore open dropdowns / mega menus).
      const barBottom = rect.top + header.offsetHeight;
      if (barBottom > bottom) bottom = barBottom;
    }

    return Math.max(0, Math.round(bottom));
  };

  const getNavEl = () => document.querySelector("[data-si26-nav]");

  const syncNavStickyTop = () => {
    const nav = getNavEl();
    const stickySection = nav?.closest(".shopify-section");
    const top = getChromeBottom();
    if (stickySection) stickySection.style.top = `${top}px`;
    document.documentElement.style.setProperty("--si26-chrome-bottom", `${top}px`);
    return top;
  };

  /**
   * Offset = chrome + nav height.
   * Never use nav.getBoundingClientRect().bottom while nav is still in document
   * flow (that value can be hundreds of px mid-page and breaks jump targets).
   */
  const getScrollOffset = () => {
    const chrome = syncNavStickyTop();
    const nav = getNavEl();
    if (!nav) return chrome;

    const navHeight = Math.round(nav.offsetHeight || 0);
    const rect = nav.getBoundingClientRect();
    const stuck = rect.top <= chrome + 2 && rect.bottom > chrome;

    if (stuck) {
      return Math.max(0, Math.round(rect.bottom));
    }
    return Math.max(0, chrome + navHeight);
  };

  const applyScrollMargin = (offset) => {
    document.documentElement.style.setProperty("--si26-scroll-margin", `${offset}px`);
    ["#si26-lottery", "#si26-products", "#si26-brand", "#si26-cards"].forEach((sel) => {
      const el = document.querySelector(sel);
      if (el) el.style.scrollMarginTop = `${offset}px`;
    });
  };

  /** Prefer pin/sticky so section padding does not leave a gap under the nav. */
  const resolveScrollElement = (element) => {
    if (!element) return null;
    return (
      element.querySelector("[data-si26-pin]") ||
      element.querySelector("[data-si26-sticky]") ||
      element.querySelector(".si26-lottery__inner") ||
      element
    );
  };

  window.si26Sticky = {
    getChromeBottom,
    syncNavStickyTop,
    getScrollOffset,
    applyScrollMargin,
    resolveScrollElement
  };

  const initNavigation = (nav) => {
    if (!nav || nav.dataset.initialized === "true") return;

    const buttons = [...nav.querySelectorAll("[data-si26-target]")];
    if (!buttons.length) return;
    nav.dataset.initialized = "true";

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const targets = buttons
      .map((button) => {
        const element = document.querySelector(button.dataset.si26Target);
        return element ? { button, element } : null;
      })
      .filter(Boolean);

    if (!targets.length) return;

    let activeIndex = 0;
    let ticking = false;
    let lockActiveUntil = 0;
    let correctTimer = 0;
    let scrollingProgrammatically = false;

    const setActive = (index) => {
      if (index < 0 || index >= targets.length) return;
      activeIndex = index;
      targets.forEach(({ button }, i) => {
        button.classList.toggle("is-active", i === index);
      });
    };

    const measureTargetTop = (element) => {
      const offset = getScrollOffset();
      applyScrollMargin(offset);
      const scrollEl = resolveScrollElement(element) || element;
      // +1 helps engage sticky pins (brand / products) under the nav.
      return Math.max(0, window.scrollY + scrollEl.getBoundingClientRect().top - offset + 1);
    };

    const updateActiveFromScroll = () => {
      if (scrollingProgrammatically || Date.now() < lockActiveUntil) return;
      const threshold = getScrollOffset() + 8;
      let nextActive = 0;

      targets.forEach(({ element }, index) => {
        const el = resolveScrollElement(element) || element;
        if (el.getBoundingClientRect().top <= threshold) {
          nextActive = index;
        }
      });

      if (nextActive !== activeIndex) setActive(nextActive);
    };

    const onScrollOrResize = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        syncNavStickyTop();
        applyScrollMargin(getScrollOffset());
        updateActiveFromScroll();
        ticking = false;
      });
    };

    const correctScrollTo = (element) => {
      const adjusted = measureTargetTop(element);
      if (Math.abs(window.scrollY - adjusted) > 2) {
        window.scrollTo({ top: adjusted, behavior: "auto" });
      }
    };

    const runSettlePass = (element) => {
      let passes = 0;
      const tick = () => {
        // Re-sync chrome (announcement may have left/re-entered during the jump).
        syncNavStickyTop();
        correctScrollTo(element);
        passes += 1;
        if (passes < 30) {
          window.requestAnimationFrame(tick);
        } else {
          scrollingProgrammatically = false;
          lockActiveUntil = Date.now() + 120;
          updateActiveFromScroll();
        }
      };
      window.requestAnimationFrame(tick);
    };

    const scrollToSection = (index) => {
      const target = targets[index];
      if (!target) return;

      window.clearTimeout(correctTimer);
      scrollingProgrammatically = true;
      lockActiveUntil = Date.now() + 1800;
      setActive(index);

      applyScrollMargin(getScrollOffset());

      // Always ease when allowed; settle pass below corrects drift from brand/products pins
      // and announcement chrome changing mid-scroll.
      const useSmooth = !reducedMotion;
      const top = measureTargetTop(target.element);

      window.scrollTo({
        top,
        behavior: useSmooth ? "smooth" : "auto"
      });

      let settled = false;
      const startSettle = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(correctTimer);
        runSettlePass(target.element);
      };

      if (useSmooth && "onscrollend" in window) {
        window.addEventListener("scrollend", startSettle, { once: true });
        // Fallback if scrollend never fires (some browsers / interrupted scrolls).
        correctTimer = window.setTimeout(startSettle, 1400);
      } else {
        correctTimer = window.setTimeout(startSettle, useSmooth ? 700 : 32);
      }
    };

    buttons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        const targetIndex = targets.findIndex(({ button: item }) => item === button);
        if (targetIndex < 0) return;
        scrollToSection(targetIndex);
      });
    });

    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    syncNavStickyTop();
    applyScrollMargin(getScrollOffset());
    setActive(0);
    updateActiveFromScroll();
  };

  const init = (scope = document) => {
    if (scope.matches?.("[data-si26-nav]")) initNavigation(scope);
    scope.querySelectorAll?.("[data-si26-nav]").forEach(initNavigation);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
